import React, { useMemo, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const AppraisalFilterForm = ({ visibleData, handleFilterSubmit, handleSearchChange }) => {
    const initialValues = {
        make: 'all',
        model: 'all',
        year: 'all',
        status: 'all',
        location: 'all',
        vin: '',
        vehicle_status: 'all',
        vehicle_name: '',
        dateFrom: null,
        dateTo: null,
        phone: ''
    };

    const [selectedMake, setSelectedMake] = useState("all");
    const [selectedModel, setSelectedModel] = useState("all");

    // ✅ Build data indexes once
    const indexData = useMemo(() => {
        if (!visibleData || visibleData.length === 0) return {
            makes: [],
            modelsByMake: {},
            yearsByMakeModel: {},
            statuses: [],
            locations: []
        };

        const makes = new Set();
        const modelsByMake = {};
        const yearsByMakeModel = {};
        const statuses = new Set();
        const locations = new Set();

        for (const item of visibleData) {
            const make = item.make?.trim();
            const model = item.model?.trim();
            const year = item.year;
            const status = item.status?.trim();
            const location = item.location?.trim();

            if (make) {
                makes.add(make);
                if (!modelsByMake[make]) modelsByMake[make] = new Set();
                if (model) modelsByMake[make].add(model);

                const key = `${make}-${model || "all"}`;
                if (!yearsByMakeModel[key]) yearsByMakeModel[key] = new Set();
                if (year) yearsByMakeModel[key].add(year);
            }

            if (status) statuses.add(status);
            if (location) locations.add(location);
        }

        return {
            makes: Array.from(makes),
            modelsByMake,
            yearsByMakeModel,
            statuses: Array.from(statuses),
            locations: Array.from(locations)
        };
    }, [visibleData]);

    // ⚡ Models and Years now read from index (no filtering)
    const modelOptions = useMemo(() => {
        if (selectedMake === 'all') {
            // merge all models from all makes
            const allModels = Object.values(indexData.modelsByMake).flatMap(set => Array.from(set));
            return [...new Set(allModels)];
        }
        return Array.from(indexData.modelsByMake[selectedMake] || []);
    }, [selectedMake, indexData]);

    const yearOptions = useMemo(() => {
        const key = `${selectedMake}-${selectedModel}`;
        const set = indexData.yearsByMakeModel[key] || new Set();
        return Array.from(set).sort((a, b) => b - a);
    }, [selectedMake, selectedModel, indexData]);

    return (
        <div className='arl-box'>
            <div className='ar-sm-title text-uppercase mb-3'>Search Filter</div>
            <Formik
                initialValues={initialValues}
                onSubmit={(values) => {
                    // console.log("Filter From values", values)
                    handleFilterSubmit(values);
                }}
            >
                {({ values, setFieldValue }) => (
                    <Form className='custom-form ar-common-form'>
                        {/* {console.log("values Status", values.status)} */}
                        {/* Vehicle Make */}
                        <div className='form-group'>
                            <label>Vehicle Make</label>
                            <Field
                                as='select'
                                name='make'
                                className='form-control'
                                onChange={(e) => {
                                    const make = e.target.value;
                                    setFieldValue("make", make);
                                    setFieldValue("model", "all");
                                    setSelectedMake(make);
                                    setSelectedModel("all");
                                }}
                            >
                                <option value="all">All</option>
                                {indexData.makes.map((m, i) => (
                                    <option key={i} value={m}>{m}</option>
                                ))}
                            </Field>
                        </div>

                        {/* Vehicle Model */}
                        <div className='form-group'>
                            <label>Vehicle Model</label>
                            <Field
                                as='select'
                                name='model'
                                className='form-control'
                                onChange={(e) => {
                                    const model = e.target.value;
                                    setFieldValue("model", model);
                                    setSelectedModel(model);
                                }}
                            >
                                <option value="all">All</option>
                                {modelOptions.map((m, i) => (
                                    <option key={i} value={m}>{m}</option>
                                ))}
                            </Field>
                        </div>

                        {/* Vehicle Year */}
                        <div className='form-group'>
                            <label>Vehicle Year</label>
                            <Field as='select' name='year' className='form-control'>
                                <option value="all">All</option>
                                {yearOptions.map((y, i) => (
                                    <option key={i} value={y}>{y}</option>
                                ))}
                            </Field>
                        </div>

                        {/* Appraisal Status */}
                        <div className='form-group'>
                            <label>Appraisal Status</label>
                            <Field as='select' name='status' className='form-control'>
                                <option value="all">All</option>
                                {indexData.statuses.map((s, i) => (
                                    <option key={i} value={s}>{s}</option>
                                ))}
                            </Field>
                        </div>

                        {/* Location */}
                        <div className='form-group'>
                            <label>Location</label>
                            <Field as='select' name='location' className='form-control'>
                                <option value="all">All</option>
                                {indexData.locations.map((l, i) => (
                                    <option key={i} value={l}>{l}</option>
                                ))}
                            </Field>
                        </div>

                        {/* VIN */}
                        <div className='form-group'>
                            <label>Search by VIN</label>
                            <Field
                                name='vin'
                                type='text'
                                className='form-control'
                                placeholder='Enter VIN'
                            />
                        </div>

                        {/* Vehicle Status */}
                        <div className='form-group'>
                            <label>Vehicle Status</label>
                            <Field as='select' name='vehicle_status' className='form-control'>
                                <option value="all">All</option>
                            </Field>
                        </div>

                        {/* Keyword */}
                        <div className='form-group'>
                            <label>Search by Keyword</label>
                            <Field
                                name='vehicle_name'
                                type='text'
                                className='form-control'
                                placeholder='Enter Keyword'
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFieldValue('vehicle_name', val);
                                    handleSearchChange?.(val);
                                }}
                            />
                        </div>

                        {/* Date From / To */}
                        <div className='form-group'>
                            <div className='row'>
                                <div className='col-6'>
                                    <label>Date From</label>
                                    <DatePicker
                                        selected={values.dateFrom}
                                        onChange={(date) => setFieldValue('dateFrom', date)}
                                        className='form-control icon-calendar-field'
                                        placeholderText='mm/dd/yy'
                                    />
                                </div>
                                <div className='col-6'>
                                    <label>Date To</label>
                                    <DatePicker
                                        selected={values.dateTo}
                                        onChange={(date) => setFieldValue('dateTo', date)}
                                        className='form-control icon-calendar-field'
                                        placeholderText='mm/dd/yy'
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Phone */}
                        <div className='form-group'>
                            <label>Search by Phone</label>
                            <Field
                                name='phone'
                                type='text'
                                className='form-control'
                                placeholder='Enter Phone'
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setFieldValue('phone', val);
                                    handleSearchChange?.(val);
                                }}
                            />
                        </div>

                        {/* Submit */}
                        <div className='mt-5'>
                            <button type='submit' className='blue-btn text-uppercase w-100'>
                                Search
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AppraisalFilterForm;
